import 'antd/dist/reset.css';
import React, { useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Tabs, Button, Spin } from "antd";
import { LogoutOutlined } from '@ant-design/icons';
import api from './api/axios';
import logo from './logo.jpg';
import leftImage from './diligent_hive.png';
import PurchaseInvoice from "./pages/PurchaseInvoice";
import Payments from "./pages/Payments";
import Quotation from "./pages/Quotation";
import SalesInvoice from "./pages/SalesInvoice";
import Receipts from "./pages/Receipts";
import SetupSuppliersCustomers from "./pages/SetupSuppliersCustomers";
import Reports from "./pages/Reports";
import Login from "./pages/Login";
import AdHocPayments from "./pages/AdHocPayments";
import PurchaseOrder from "./pages/PurchaseOrder";
import GenSalesInvoice from "./pages/GenSalesInvoice";
import Merge_PDF from './pages/Merge_PDF';
import Folders from './pages/Folders';
import ReminderMails from './pages/ReminderMails';
import AuditLog from './pages/AuditLog';
import RefreshButton from './pages/RefreshButton';

/*
const tabList = [
    { key: "incoming-invoice", label: "Purchase Invoice", path: "/incoming-invoice" },
    { key: "payments", label: "Purchase Payments", path: "/payments" },
    { key: "quotation", label: "Quotation", path: "/quotation" },
    { key: "purchaseorder", label: "Purchase Order", path: "/purchaseorder" },
    { key: "outgoing-invoice", label: "Sales Invoice", path: "/outgoing-invoice" },
    { key: "remindermails", label: "ReminderMails", path: "/remindermails" },
    { key: "receipts", label: "Sales Receipts", path: "/receipts" },
    { key: "adhocpayments", label: "AdHoc Payments", path: "/adhocpayments" },
    { key: "reports", label: "Reports", path: "/reports" },
    { key: "setup", label: "Suppliers Customers PO", path: "/setup" },
    { key: "gensalesinvoice", label: "Gen Sales Invoice", path: "/gensalesinvoice" },
    { key: "mergepurchaseinvoice", label: "Merge Purchase Invoice", path: "/mergepurchaseinvoice" },
    { key: "folders", label: "Folders", path: "/folders" },
    { key: "auditlog", label: "AuditLog", path: "/auditlog" },
];
*/
// 5 Main Groups
const menuGroups = [
    {
        label: "📥 Purchases",
        children: [
            { key: "incoming-invoice", label: "Purchase Invoice", path: "/incoming-invoice" },
            { key: "payments", label: "Purchase Payments", path: "/payments" },
            { key: "purchaseorder", label: "Purchase Order", path: "/purchaseorder" },
            { key: "mergepurchaseinvoice", label: "Merge PDF", path: "/mergepurchaseinvoice" }
        ]
    },
    {
        label: "📤 Sales",
        children: [
            { key: "outgoing-invoice", label: "Sales Invoice", path: "/outgoing-invoice" },
            { key: "receipts", label: "Sales Receipts", path: "/receipts" },
            { key: "gensalesinvoice", label: "Gen Bulk Sales Invoice", path: "/gensalesinvoice" },
            { key: "quotation", label: "Quotation", path: "/quotation" }
        ]
    },
    {
        label: "💰 Finance",
        children: [
            { key: "adhocpayments", label: "AdHoc Payments", path: "/adhocpayments" },
            { key: "remindermails", label: "Reminder Mails", path: "/remindermails" }
        ]
    },
    {
        label: "⚙️ Setup",
        children: [
            { key: "setup", label: "Suppliers Customers PO", path: "/setup" }
        ]
    },
    {
        label: "📊 Reports",
        children: [
            { key: "reports", label: "Dashboard", path: "/reports" },
            { key: "folders", label: "Folders", path: "/folders" },
            { key: "auditlog", label: "Audit Log", path: "/auditlog" }
        ]
    }
];


function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loading, setLoading] = useState(true);
    const [skipVerify, setSkipVerify] = useState(false);

    useEffect(() => {
        const initAuth = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                setIsLoggedIn(false);
                setLoading(false);
                return;
            }

            // Skip verify if just logged in
            if (skipVerify) {
                setIsLoggedIn(true);
                setLoading(false);
                return;
            }

            try {
                await api.get('/api/verify');
                setIsLoggedIn(true);
            } catch {
                localStorage.removeItem('token');
                setIsLoggedIn(false);
            } finally {
                setLoading(false);
            }
        };

        initAuth();
    }, [skipVerify]);  // ✅ Depend on skipVerify

    // Tab close cleanup (sessionStorage version)
    useEffect(() => {
        if (!isLoggedIn) return; // Only when logged in

        // Set session flag
        sessionStorage.setItem('activeTab', 'true');

        const handleBeforeUnload = () => {
            if (sessionStorage.getItem('activeTab') === 'true') {
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('pagehide', handleBeforeUnload); // Better for tabs

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('pagehide', handleBeforeUnload);
        };
    }, [isLoggedIn]); // Only when logged in

    const handleLoginSuccess = () => {
        setSkipVerify(true);  // ✅ Skip next verify
        setIsLoggedIn(true);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        setIsLoggedIn(false);
        setSkipVerify(false);  // Reset
        window.location.href = '/login';
    };

    if (loading) {
        return <Spin center size="large" />;
    }

    if (!isLoggedIn) {
        return <Login onLoginSuccess={handleLoginSuccess} />;  // ✅ Pass callback
    }

    /*
    // TabNav Component (defined INSIDE App - fixes scope)
    const TabNav = () => {
        const location = useLocation();
        const navigate = useNavigate();
        const cleanedPath = location.pathname.replace(/\/+$/, '');
        const current = tabList.find(tab => tab.path === cleanedPath)?.key;

        React.useEffect(() => {
            if (!current) {
                navigate("/incoming-invoice", { replace: true });
            }
        }, [current, navigate]);

        if (!current) return null;

        return (
            <Tabs
                className="main-tabs"
                activeKey={current}
                onChange={key => {
                    const tab = tabList.find(t => t.key === key);
                    if (tab) navigate(tab.path);
                }}
                items={tabList.map(tab => ({
                    key: tab.key,
                    label: tab.label,
                }))}
                tabBarExtraContent={
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <RefreshButton />
                        <Button
                            size="small"
                            danger
                            icon={<LogoutOutlined />}
                            onClick={handleLogout}
                            style={{ marginLeft: 0 }}
                        >
                            Logout
                        </Button>
                    </div>
                }
            />
        );
    };
    */
    const TabNav = () => {
        const location = useLocation();
        const navigate = useNavigate();
        const [activeGroup, setActiveGroup] = useState(null);
        const [timeoutId, setTimeoutId] = useState(null);
        const dropdownRef = useRef(null);

        const cleanedPath = location.pathname.replace(/\/+$/, '');
        const currentItem = menuGroups
            .flatMap(group => group.children.map(child => ({ ...child, groupLabel: group.label })))
            .find(item => item.path === cleanedPath);

        const hideDropdown = useCallback(() => {
            if (timeoutId) clearTimeout(timeoutId);
            setTimeout(() => setActiveGroup(null), 300);  // ↑ Increased delay
        }, [timeoutId]);

        const showDropdown = useCallback((groupIndex) => {
            if (timeoutId) clearTimeout(timeoutId);
            setActiveGroup(groupIndex);

            const id = setTimeout(hideDropdown, 4000);  // 3 sec auto-hide
            setTimeoutId(id);
        }, [timeoutId, hideDropdown]);

        useEffect(() => {
            const handleClickOutside = (event) => {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                    hideDropdown();
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, [hideDropdown]);

        // ✅ CLICK GROUP → Load default page
        const handleGroupClick = (group) => {
            const defaultPage = group.children[0];  // First child = default
            navigate(defaultPage.path);
            showDropdown(menuGroups.findIndex(g => g.label === group.label));  // Keep open 3s
        };

        if (!currentItem) {
            navigate(menuGroups[0].children[0].path, { replace: true });
            return null;
        }

        return (
            <div className="menu-container" ref={dropdownRef} style={{ position: 'relative' }}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    {menuGroups.map((group, index) => (
                        <div
                            key={group.label}
                            className={`menu-group-wrapper`}
                            style={{
                                position: 'relative',
                                display: 'inline-block'
                            }}
                        >
                            {/* Group Header */}
                            <div
                                className="menu-group"
                                onClick={() => handleGroupClick(group)}
                                onMouseEnter={() => showDropdown(index)}
                                style={{
                                    cursor: 'pointer',
                                    padding: '12px 64px',
                                    borderRadius: 8,
                                    background: currentItem?.groupLabel === group.label ? '#1890ff' : '#f5f5f5',
                                    color: currentItem?.groupLabel === group.label ? '#ffffff' : '#000000',
                                    transition: 'all 0.3s',
                                    minWidth: 120,
                                    textAlign: 'center'
                                }}
                            >
                                <span style={{ fontWeight: 600, fontSize: 18 }}>{group.label}</span>
                            </div>

                            {/* Dropdown - FULL WIDTH hover area */}
                            {activeGroup === index && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        background: 'white',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: 8,
                                        boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
                                        zIndex: 1001,
                                        marginTop: -1,  // Overlap to eliminate gap
                                        maxHeight: 320,
                                        overflow: 'hidden'
                                    }}
                                >
                                    <div  // ← Invisible hover shield
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            zIndex: 1
                                        }}
                                        onMouseEnter={() => { if (timeoutId) clearTimeout(timeoutId); }}
                                        onMouseLeave={hideDropdown}
                                    />

                                    <div style={{ position: 'relative', zIndex: 2 }}>
                                        {group.children.map(child => (
                                            <div
                                                key={child.key}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(child.path);
                                                    setTimeout(hideDropdown, 200);  // Brief delay
                                                }}
                                                style={{
                                                    padding: '14px 20px',
                                                    cursor: 'pointer',
                                                    borderBottom: '1px solid #f0f0f0',
                                                    background: child.path === cleanedPath ? '#f0f0f0' : 'transparent',
                                                    color: '#000000',
                                                    fontWeight: child.path === cleanedPath ? 600 : 400,
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (child.path !== cleanedPath) {
                                                        e.target.style.background = '#f8f8f8';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (child.path !== cleanedPath) {
                                                        e.target.style.background = 'transparent';
                                                    }
                                                }}
                                            >
                                                {child.label}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}

                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                        <RefreshButton />
                        <Button size="small" danger icon={<LogoutOutlined />} onClick={handleLogout}>
                            Logout
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <BrowserRouter>
            <div className="logo-bar" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 20px',
                background: '#fff'
            }}>
                <img src={leftImage} alt="" style={{
                    height: 100,
                    width: 'auto',
                    objectFit: 'contain',
                    maxWidth: '250px'
                }} />
                <img src={logo} alt="Company Logo" style={{
                    height: 90,
                    width: 'auto',
                    objectFit: 'contain',
                    maxWidth: '250px'
                }} />
            </div>

            <div className="app-main-container">
                <TabNav />
                <Routes>
                    <Route path="/incoming-invoice" element={<PurchaseInvoice />} />
                    <Route path="/payments" element={<Payments />} />
                    <Route path="/quotation" element={<Quotation />} />
                    <Route path="/purchaseorder" element={<PurchaseOrder />} />
                    <Route path="/outgoing-invoice" element={<SalesInvoice />} />
                    <Route path="/remindermails" element={<ReminderMails />} />
                    <Route path="/receipts" element={<Receipts />} />
                    <Route path="/adhocpayments" element={<AdHocPayments />} />
                    <Route path="/setup" element={<SetupSuppliersCustomers />} />
                    <Route path="/reports" element={<Reports />} />
                    <Route path="/folders" element={<Folders />} />
                    <Route path="/gensalesinvoice" element={<GenSalesInvoice />} />
                    <Route path="/mergepurchaseinvoice" element={<Merge_PDF />} />
                    <Route path="/auditlog" element={<AuditLog />} />
                    <Route path="/" element={<Navigate to="/incoming-invoice" replace />} />
                    <Route path="*" element={<Navigate to="/incoming-invoice" replace />} />
                </Routes>
            </div>
        </BrowserRouter>
    );
}

export default App;
